<script>
import * as d3 from "d3";
import { OrgChart } from "d3-org-chart";

export default {
  name: "NetworkMatriz",

  props: {
    data: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      chart: null,
      rawData: null,
      // A Matriz abre filtrada (somente Regionais); para ver os consultores o
      // usuário desmarca o filtro.
      showOnlyRegionals: true,
    };
  },

  computed: {
    svgHeight() {
      return window.innerHeight - 320;
    },
  },

  watch: {
    data(value) {
      if (value && value.length) {
        this.rawData = value;
        this.renderChart(this.currentData());
      }
    },
  },

  methods: {
    /* ===============================
        FILTRO: SOMENTE REGIONAIS
    =============================== */
    buildRegionalsOnlyData(all) {
      const byId = new Map(all.map(n => [String(n.id), n]));
      const isRegional = n => (n?.type || "").toLowerCase() === "regional";
      // A raiz (usuário logado) fica sempre, mesmo que não seja Regional:
      // sem ela o OrgChart recebe várias raízes e não renderiza.
      const isRoot = n => n.parentId == null || !byId.has(String(n.parentId));
      const keep = n => isRegional(n) || isRoot(n);

      const findParentRegionalId = (node) => {
        let parentId = node.parentId;
        while (parentId != null) {
          const parent = byId.get(String(parentId));
          if (!parent) break;
          if (keep(parent)) return parent.id;
          parentId = parent.parentId;
        }
        return null;
      };

      return all
        .filter(keep)
        .map(r => {
          const parentRegionalId = findParentRegionalId(r);
          return {
            ...r,
            ...(parentRegionalId ? { parentId: parentRegionalId } : {}),
          };
        });
    },

    currentData() {
      const base = this.rawData || [];
      return this.showOnlyRegionals
        ? this.buildRegionalsOnlyData(base)
        : base;
    },

    toggleOnlyRegionals() {
      this.showOnlyRegionals = !this.showOnlyRegionals;
      this.renderChart(this.currentData());
    },

    /* ===============================
        RENDER DO GRFICO
    =============================== */
    renderChart(data) {
      if (this.chart) {
        d3.select("#matriz").select("svg").remove();
      }

      this.chart = new OrgChart();

      this.chart
        .container("#matriz")
        .data(data)
        .svgHeight(this.svgHeight)
        .compact(false)

        /*  Cards maiores */
        .nodeWidth(() => 286)   // +30%
        .nodeHeight(() => 150)
        .childrenMargin(() => 90)
        .setActiveNodeCentered(true)

        /*  Linhas */
        .linkUpdate(function () {
          d3.select(this)
            .attr("stroke", "#cbd5f5")
            .attr("stroke-width", 2);
        })

        /*  Boto expand / collapse COM contador */
        .buttonContent(({ node }) => {
          const total = node.data._directSubordinates || 0;

          return `
            <div style="
              background:#0f172a;
              color:#ffffff;
              min-width:36px;
              height:26px;
              padding:0 8px;
              border-radius:999px;
              display:flex;
              align-items:center;
              justify-content:center;
              gap:4px;
              font-size:12px;
              font-weight:700;
              box-shadow:0 6px 14px rgba(0,0,0,.25);
              cursor:pointer;
              user-select:none;
            ">
              <span style="font-size:14px;line-height:1;">
                ${node.children ? "−" : "+"}
              </span>
              <span style="opacity:.9;">
                ${total}
              </span>
            </div>
          `;
        })

        /*  Card */
        .nodeContent((d) => {
          const level = d.depth;
          const type = d.data.type || "Consultor";
          const color = type === "Regional" ? "#2563EB" : "#10B981";

          return `
            <div style="
              width:260px;
              background:var(--st-surface);
              border-radius:14px;
              padding:14px 16px;
              box-shadow:0 10px 25px rgba(0,0,0,.08);
              border-left:6px solid ${color};
              font-family:Inter, system-ui, sans-serif;
            ">

              <div style="display:flex;align-items:flex-start;gap:12px;">
                <img
                  src="${d.data.avatar}"
                  style="
                    width:44px;
                    height:44px;
                    border-radius:50%;
                    object-fit:cover;
                    border:2px solid ${color};
                    flex-shrink:0;
                  "
                />

                <div style="min-width:0;">
                  <!--  Nome com quebra real -->
                  <div style="
                    font-weight:700;
                    font-size:14px;
                    color:#0f172a;
                    line-height:1.25;
                    white-space:normal;
                    word-break:break-word;
                    display:-webkit-box;
                    -webkit-line-clamp:3;
                    -webkit-box-orient:vertical;
                    overflow:hidden;
                  ">
                    ${d.data.name}
                  </div>

                  <div style="font-size:12px;color:#475569;margin-top:2px;">
                    ${type}
                  </div>
                </div>
              </div>

              <div style="
                margin-top:10px;
                font-size:11px;
                color:#64748b;
                text-align:right;
              ">
                Nível ${level}
              </div>
            </div>
          `;
        })

        .render();
    },

    /* ===============================
        CONTROLES
    =============================== */
    zoomIn() { this.chart.zoomIn(); },
    zoomOut() { this.chart.zoomOut(); },
    fit() { this.chart.fit().render(); },
    expandAll() { this.chart.expandAll().fit(); },
    collapseAll() { this.chart.collapseAll().fit(); },
    fullscreen() { this.chart.fullscreen(); },
  },
};
</script>

<template>
  <div class="network-wrapper">
    <div class="network-controls">
      <button @click="zoomOut"><i class="bx bx-minus-circle"></i></button>
      <button @click="zoomIn"><i class="bx bx-plus-circle"></i></button>
      <button @click="fit"><i class="bx bx-align-middle"></i></button>
      <button @click="expandAll"><i class="bx bx-expand"></i></button>
      <button @click="collapseAll"><i class="bx bx-collapse"></i></button>
      <button @click="fullscreen"><i class="bx bx-fullscreen"></i></button>

      <!--  FILTRO -->
      <button
        @click="toggleOnlyRegionals"
        :class="{ active: showOnlyRegionals }"
        :title="showOnlyRegionals ? 'Mostrando somente Regionais — clique para ver os consultores' : 'Mostrar somente Regionais'"
        :aria-pressed="showOnlyRegionals ? 'true' : 'false'"
      >
        <i class="bx bx-filter-alt"></i>
      </button>
    </div>

    <div id="matriz"></div>
  </div>
</template>

<style scoped>
.network-wrapper {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px;
}

.network-controls {
  text-align: center;
  margin-bottom: 12px;
}

.network-controls button {
  background: #1e293b;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 0 4px;
  cursor: pointer;
}

.network-controls button.active {
  background: #2563eb;
}

.network-controls button i {
  font-size: 18px;
}

.network-controls button:hover {
  background: #334155;
}

.network-controls button.active:hover {
  background: #1d4ed8;
}
</style>
